import {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createTaxConfig, updateTaxConfig, deleteTaxConfig} from '../../api/taxConfig';
import {findAllAccounts} from '../../api/capitalAccount';
import {findAllProjects} from '../../api/project';
import {findAllCompanies} from '../../api/company';
import {selectTaxConfigs, addTaxConfig, updateTaxConfigById, removeTaxConfigById} from '../../store/taxConfigs';
import {setAccounts} from '../../store/accounts';
import {setProjects} from '../../store/projects';
import {setCompanies} from '../../store/companies';
import Modal from '../../components/common/Modal';
import WalletSelect from '../../components/common/WalletSelect/WalletSelect';
import './TaxConfigPage.scss';
import {formatNumber} from '../../utils';

const syncRelated = (dispatch) =>
    Promise.all([findAllAccounts(), findAllProjects(), findAllCompanies()])
        .then(([accounts, projects, companies]) => {
            dispatch(setAccounts(accounts));
            dispatch(setProjects(projects));
            dispatch(setCompanies(companies));
        });

const PERIOD_TYPES = ['MONTH', 'QUARTER'];
const PERIOD_TYPE_OPTIONS = PERIOD_TYPES.map(t => ({value: t, label: t}));

const emptyForm = () => ({
    startMonth: '',
    periodType: 'MONTH',
    taxPercent: '',
    fixedTaxUah: '',
});

const configToForm = (c) => ({
    startMonth: c.startMonth?.slice(0, 7) ?? '',
    periodType: c.periodType ?? 'MONTH',
    taxPercent: String(c.taxPercent ?? ''),
    fixedTaxUah: String(c.fixedTaxUah ?? ''),
});

const formToPayload = (f) => ({
    startMonth: f.startMonth ? `${f.startMonth}-01` : null,
    periodType: f.periodType,
    taxPercent: Number(f.taxPercent),
    fixedTaxUah: Number(f.fixedTaxUah),
});

const effectiveTo = (configs, index) => {
    const next = index === 0 ? null : configs[index - 1];
    if (!next) return 'present';
    const d = new Date(next.startMonth);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 7);
};

const TaxConfigPage = () => {
    const dispatch = useDispatch();
    const rawConfigs = useSelector(selectTaxConfigs);
    const configs = useMemo(
        () => [...rawConfigs].sort((a, b) => new Date(b.startMonth) - new Date(a.startMonth)),
        [rawConfigs],
    );

    const [form, setForm] = useState(emptyForm());
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const openCreate = () => {
        setForm(emptyForm());
        setEditId(null);
        setShowForm(true);
    };

    const openEdit = (c) => {
        setForm(configToForm(c));
        setEditId(c.id);
        setShowForm(true);
    };

    const cancel = () => {
        setShowForm(false);
        setEditId(null);
        setForm(emptyForm());
    };

    const save = () => {
        setSaving(true);
        const payload = formToPayload(form);
        const req = editId ? updateTaxConfig(editId, payload) : createTaxConfig(payload);
        req.then(saved => {
            cancel();
            if (editId) dispatch(updateTaxConfigById(saved));
            else dispatch(addTaxConfig(saved));
            syncRelated(dispatch);
        }).finally(() => setSaving(false));
    };

    const remove = (id) => {
        if (!window.confirm('Delete this tax config? Income tax for the covered period will be cleared.')) return;
        deleteTaxConfig(id).then(() => {
            dispatch(removeTaxConfigById(id));
            syncRelated(dispatch);
        });
    };

    const canSubmit = form.startMonth && form.periodType && form.taxPercent !== '' && form.fixedTaxUah !== '';

    function renderTaxPeriod(c, index) {
        const next = index === 0 ? null : configs[index - 1];

        if (next && c.periodType === 'MONTH') {
            return c.startMonth?.slice(0, 7);
        }

        if (next && c.periodType === 'QUARTER') {
            const month = c.startMonth?.slice(5, 7) ?? '-';
            const year = c.startMonth?.slice(0, 4) ?? '-';

            switch (month) {
                case '01': return 'Q1 ' + year;
                case '04': return 'Q2 ' + year;
                case '07': return 'Q3 ' + year;
                case '10': return 'Q4 ' + year;
                default:   return `${c.startMonth?.slice(0, 7)} → ${effectiveTo(configs, index)}`;
            }
        }

        return `${c.startMonth?.slice(0, 7)}`;
    }

    return (
        <div className="capital-page">
            <div className="capital-page__header">
                <h2 className="capital-page__title">Tax Configuration</h2>
                <button className="wallet-add-button" onClick={openCreate}>
                    + Config
                </button>
            </div>

            {showForm && (
                <Modal onClose={cancel}>
                <form className="tax-form"
                      onSubmit={(e) => { e.preventDefault(); if (canSubmit) save(); }}>
                    <h3 className="tax-form__title">{editId ? 'Edit Tax Config' : 'Add Tax Config'}</h3>

                    <div className="tax-form__row">
                        <div className="tax-form__field">
                            <label className="tax-form__label">Start Month</label>
                            <input className="tax-form__input" type="month" value={form.startMonth}
                                   onChange={e => setForm(f => ({...f, startMonth: e.target.value}))} required/>
                        </div>
                        <div className="tax-form__field">
                            <label className="tax-form__label">Period Type</label>
                            <WalletSelect
                                options={PERIOD_TYPE_OPTIONS}
                                value={PERIOD_TYPE_OPTIONS.find(o => o.value === form.periodType) ?? null}
                                onChange={opt => setForm(f => ({...f, periodType: opt?.value ?? ''}))}
                                isClearable={false}
                            />
                        </div>
                    </div>

                    <div className="tax-form__row">
                        <div className="tax-form__field">
                            <label className="tax-form__label">Tax %</label>
                            <input className="tax-form__input" type="number" step="0.01" placeholder="5.00"
                                   value={form.taxPercent}
                                   onChange={e => setForm(f => ({...f, taxPercent: e.target.value}))} required/>
                        </div>
                        <div className="tax-form__field">
                            <label className="tax-form__label">Fixed Tax (UAH)</label>
                            <input className="tax-form__input" type="number" step="0.01" placeholder="0.00"
                                   value={form.fixedTaxUah}
                                   onChange={e => setForm(f => ({...f, fixedTaxUah: e.target.value}))} required/>
                        </div>
                    </div>

                    <div className="tax-form__actions">
                        <button type="button" className="tax-form__btn" onClick={cancel}>Cancel</button>
                        <button type="submit" className="tax-form__btn tax-form__btn--primary"
                                disabled={!canSubmit || saving}>
                            {saving ? 'Saving…' : editId ? 'Save' : 'Add'}
                        </button>
                    </div>
                </form>
                </Modal>
            )}

            <div className="scrollable-list-holder">
                {configs.length === 0 && <p className="tax-config-empty">No tax configurations yet.</p>}
                {configs.map((c, i) => (
                    <div key={c.id} className="tax-config-card"
                         onClick={() => openEdit(c)}>
                        <div className="tax-config-card__period">
                            <span className="tax-config-card__dates">
                                {renderTaxPeriod(c, i)}
                            </span>
                            <span className="tax-config-card__type">{c.periodType}</span>
                        </div>
                        <div className="tax-config-card__values">
                            <span>{formatNumber(c.taxPercent)}% tax</span>
                            <span>{formatNumber(c.fixedTaxUah)} UAH fixed</span>
                        </div>
                        <button className="btn-del" title="Delete"
                                onClick={e => { e.stopPropagation(); remove(c.id); }}>
                            ×
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TaxConfigPage;
